<template>
  <div class="dashboard-page flex flex-1 min-w-0 w-full h-full">
    <!--
      AB#21279 — with the pane expanded the list gives up its half. v-show, not
      v-if: the table keeps its scroll, its page and its selection for the
      moment the pane comes back.
    -->
    <div
      v-show="!(openDetails && listGivesWay)"
      class="flex flex-col flex-1 gap-[20px] overflow-y-auto scroll-hidden transition-all duration-300 ease-in-out pt-[12px]"
      :class="
        openDetails && !tabletList
          ? 'basis-full md:basis-1/2 px-[10px]'
          : 'basis-full  px-[10px] md:px-[30px]'
      "
    >
      <!--
        AB#21297 — the Dashboard / Calendar / My Tasks strip is gone. It cost a
        band of vertical space on every visit to switch to two views that are
        not part of the orders workflow, and the decision was to retire both
        from this page rather than tuck them into a menu.
      -->
      <div class="flex flex-col w-full flex-1 gap-[20px] h-full overflow-y-auto pb-[30px]">
        <DashboardCards
          :order_items_by_status_group="dashboardData?.order_items_by_status_group ?? []"
          :splitter="openDetails ? true : false"
          :totalOrders="dashboardData?.items.total ?? 0"
          v-if="dashboardData && !isSuperOrOrderAdmin() && permissionLoaded"
        />
        <!--
            AB#21297 — the modals that used to own a toolbar button each. They
            are mounted here with their triggers hidden and opened from the ⋯
            menu, because the menu closes on selection and would unmount
            anything rendered inside it.
          -->
        <AssignOrderAdminModal
          v-if="can('assign-order-administrator')"
          ref="assignAdminModal"
          hide-trigger
          @onSave="fetchOnSave(true)"
        />
        <ExportPriceModal
          v-if="can('export-order-prices')"
          ref="exportModal"
          hide-trigger
          :on-export="exportOrderPrices"
        />
        <DataTable
          ref="datatable"
          :update-key="`dashboard_${tableData.map((row) => row.id).join('|')}`"
          :data="tableData"
          :columns="columns"
          :pageSizeOptions="pageSizeOptions"
          density="compact"
          :pinned-columns="true"
          resizable
          :column-widths="columnWidths"
          @update:columnWidths="setColumnWidths"
          pagination-style="range"
          :footer-gutter="88"
          row_id="id"
          :isAsync="true"
          :totalItems="totalItems"
          @fetch-data="(data: any) => fetchRecords(data)"
          :currentPaginationInfo="current_filters"
          :searchable="true"
          search-placeholder="Type to search"
          :isLoading="isPending"
          :isFetching="isFetching"
          :error="error"
          @cell-click="onCellClick"
          :showPagination="true"
          :showActionsColumn="openDetails ? false : true"
          @row-right-click="openDashboardActions"
          :rowClass="
            (row) =>
              [
                selectedRow?.id === row.id ? 'bg-surface-3' : '',
                openOrderItemIds.has(row.id) ? 'dt-row-open' : '',
              ]
                .filter(Boolean)
                .join(' ')
          "
          :classProps="tableData?.length ? 'min-h-[600px]' : ''"
        >
          <!--
              AB#21297 — one band of controls, read left to right in four
              beats: which list (tabs) · find (search) · adjust (filter,
              columns, ⋯) · create. The page used to spend three bands on this
              — page tabs, buttons, then tabs-and-search — before the first
              order appeared. Only Create Order carries colour; everything
              else is an icon with a tooltip, so the eye lands on the one
              thing this row is for.
            -->
          <template #toolbar="{ searchQuery, setSearch, performSearch }">
            <div class="dash-toolbar">
              <DashboardTabs
                :tabs="dashboardTabs"
                :currentTab="current_filters?.items_for ?? null"
                @tab-change="(tab) => onTabChange(tab)"
                type="dashboard"
              />
              <!--
                  AB#21247 — Enter was the only way to run the search, and
                  nothing on screen said so. The magnifier moves out of the
                  field, where it was decoration, and onto a button, where it is
                  the same gesture made clickable. Enter still works.
                -->
              <!-- AB#21984 — the same field and button, with suggestions while typing. -->
              <DashboardSearchBox
                :model-value="searchQuery"
                @update:model-value="setSearch"
                @search="performSearch"
                @open="openSuggestedOrder"
                :can-ask="canAskElsa"
                @asked="applyAsked"
                @clear-asked="filtersPanel?.clearFilters()"
              />
              <div class="dash-toolbar-actions">
                <DashboardFilters
                  compact
                  ref="filtersPanel"
                  :currentTab="currentTab"
                  :current-mode="'dashboard'"
                  @update-filter="(filter: string | null) => getQuickFilterData(filter)"
                />
                <ColumnPicker
                  compact
                  class="dash-col-picker"
                  :options="columnOptions"
                  :hidden="hiddenColumns"
                  :always-visible-count="2"
                  :can-reset="canResetColumns"
                  @update:hidden="setHiddenColumns"
                  @reset="resetColumnWidths"
                />
                <DashboardMoreMenu
                  :can-assign-admin="can('assign-order-administrator')"
                  :can-export="can('export-order-prices')"
                  :can-shipment="can('shipment-booking')"
                  :shipment-disabled="isDisabled"
                  @assign-admin="assignAdminModal?.open()"
                  @export="exportModal?.open()"
                  @shipment-bookings="getShippmentBookingCsv"
                />
                <CreateOrderButton
                  v-if="can('manual-create-order')"
                  class="dash-toolbar-primary"
                  :can-elsa="canCreateWithElsa"
                  @manual="openManualOrder(null)"
                  @elsa="createWithElsa = true"
                />
              </div>
            </div>
          </template>
            <template #mobile="{ rows, loading, error: mobileError }">
            <div class="mobile-orders">
              <p v-if="loading" role="status">Loading orders…</p>
              <p v-else-if="mobileError" role="alert">Orders could not be loaded. Please try again.</p>
              <p v-else-if="!rows?.length" role="status">No orders match this search or filter.</p>
              <template v-else>
                <button
                  v-for="row in (rows as DashboardItem[])"
                  :key="row.id"
                  type="button"
                  class="mobile-order"
                  :aria-label="`Open order #${row.item_no || row.order?.order_no}`"
                  @click="handleCellClick({ row, column: { key: 'item_no', label: 'Order' } })"
                >
                  <span class="mobile-order-top">
                    <strong>#{{ row.item_no || row.order?.order_no }}</strong>
                    <span class="mobile-order-status">{{ CustimooOrderFlowStatuses[row.status] || row.status }}</span>
                  </span>
                  <span v-if="can('show-urgency-filters') && row.order?.urgency" class="mobile-order-priority">{{ row.order.urgency }}</span>
                  <strong class="mobile-order-customer">{{ row.order?.customer_name || row.order?.customer_email || 'Customer not recorded' }}</strong>
                  <span v-if="row.order?.customer_reference_no" class="mobile-order-reference">{{ row.order.customer_reference_no }}</span>
                  <span class="mobile-order-facts">
                    <span v-if="columns.some(col => col.key === 'items_total_quantity')">{{ row.items_total_quantity ?? '—' }} pcs</span>
                    <span v-if="columns.some(col => col.key === 'factory_name')">{{ row.factory_name || 'Factory unassigned' }}</span>
                    <span>Ships {{ row.factory_id && latestShippingPromise(row.est_shipping_date) ? formatShortDate(latestShippingPromise(row.est_shipping_date)!) : 'not set' }}</span>
                    <span v-if="row.in_hand_date">In hand {{ formatShortDate(row.in_hand_date) }}</span>
                  </span>
                  <span class="mobile-order-open">View order & actions <span aria-hidden="true">→</span></span>
                </button>
              </template>
            </div>
          </template>
          <template #actions="{ row }">
            <DashboardActions
              :current-tab="currentTab"
              @onFetch="fetchOnSave"
              @selectRow="handleRowSelected"
              @openDetails="onCellClick({ row: $event })"
              :order_item_list="row"
              :showAsMenu="false"
              :order="selectedOrderDetails"
            />
          </template>
          <!-- AB#22137 — on a phone each row is a card (DataTable's card mode). -->
          <template #card="{ row, open }">
            <DashboardOrderCard
              :row="row"
              :current-tab="currentTab"
              :show-merchant="can('order-listing-merchant-column')"
              :show-factory="can('order-listing-factory-column')"
              :open="openOrderItemIds.has(row.id)"
              @open="open"
            >
              <template #actions>
                <DashboardActions
                  :current-tab="currentTab"
                  @onFetch="fetchOnSave"
                  @selectRow="handleRowSelected"
                  @openDetails="onCellClick({ row: $event })"
                  :order_item_list="row"
                  :showAsMenu="false"
                  :order="selectedOrderDetails"
                />
              </template>
            </DashboardOrderCard>
          </template>
          <template #loading>
            <div class="flex items-center justify-center p-8">
              <div class="w-8 h-8 border-b-2 border-ink rounded-full animate-spin"></div>
              <span class="ml-2 text-ink-soft">Loading orders...</span>
            </div>
          </template>
          <template #error>
            <div class="flex items-center justify-center p-8 text-red-600">
              <span>Error loading orders. Please try again.</span>
            </div>
          </template>
        </DataTable>
      </div>
    </div>
    <div ref="dashboardActionsRef" v-if="showDashboardActions" class="absolute z-50">
      <DashboardActions
        :current-tab="currentTab"
        @onFetch="fetchOnSave"
        @selectRow="handleRowSelected(selectedRow)"
        @openDetails="onCellClick({ row: $event })"
        @openOrderPriceModal="openOrderPriceModal"
        :order_item_list="{
          ...currentRow,
          factory_products: selectedOrderDetails?.items[0]?.factory_products ?? [],
        }"
        :showAsMenu="true"
        :order="selectedOrderDetails"
        @closeMenu="handleCloseAction"
      />
    </div>
    <Transition name="slide-in">
      <div
        v-if="openDetails"
        v-show="!tabletList"
        class="dash-pane fixed inset-0 z-[10035] w-full overflow-y-auto bg-surface md:static md:z-auto md:w-auto md:min-w-0 md:overflow-visible border-l border-line flex flex-col"
        :class="listGivesWay ? 'md:basis-full' : 'md:basis-1/2'"
      >
        <!--
          AB#21279 — up to five orders open, as tabs. The strip owns expand and
          close; OrderDetail no longer draws a close button of its own.
        -->
        <OrderTabStrip
          :tabs="orderTabs.tabs"
          :active-key="orderTabs.activeKey"
          :expanded="listGivesWay"
          @activate="activateOrderTab"
          @close="closeOrderTab"
          @close-all="closeAllOrderTabs"
          @toggle-expand="onToggleExpand"
        />
        <!--
          One OrderDetail instance per tab, kept alive while the tab is open, so
          a tab keeps its inner tab, its scroll, a half-typed reason and an
          Ask ELSA read when the user switches away and back. `max` matches the
          strip's cap; a closed tab's instance is dropped by the key change.
        -->
        <KeepAlive :max="5">
          <OrderDetail
            :key="orderTabs.activeKey ?? 'none'"
            :initialTab="paneDefaultTab"
            :pendingAction="pendingAction"
            @pendingActionDone="clearPendingAction"
            :currentTab="currentTab"
            :selectedOrderDetails="selectedOrderDetails"
            :isSidepaneLoading="isSidepaneLoading"
            :selectedRow="currentRow"
            :filteredOrderDetailTabs="filteredOrderDetailTabs"
            :currentOrderItem="currentOrderItem"
            :currentOrderItemIndex="currentOrderItemIndex"
            :sidebarOpen="sidebarOpen"
            :orderAdmins="store.orderAdmins"
            @fetchOnSave="fetchOnSave"
            @handleOrderMoved="handleOrderMoved"
            @handleOrderUpdated="handleOrderUpdated"
            @openOrderPriceModal="openOrderPriceModal"
            @detailsTabChange="detailsTabChange"
            @dirtyChange="setActiveTabDirty"
            @onClose="orderTabs.activeKey && closeOrderTab(orderTabs.activeKey)"
          />
        </KeepAlive>
        <!-- The Undo offer after a sixth order displaced a tab. -->
        <Transition name="fade">
          <div v-if="lastReplacedTab" class="otab-undo" role="status">
            Closed {{ lastReplacedTab.tab.label }} to open this one
            <button type="button" class="otab-undo-btn" @click="undoLastReplace">Undo</button>
          </div>
        </Transition>
      </div>
    </Transition>

    <Transition name="fade">
      <CreateManualOrderModal
        v-if="createManualOrder"
        :prefill="elsaPrefill"
        @onSave="fetchOnSave(true)"
        @close="() => (createManualOrder = false)"
      />
    </Transition>
    <Transition name="fade">
      <CreateWithElsaDrawer
        v-if="createWithElsa"
        :viewer-name="store.user?.name ?? null"
        @close="() => (createWithElsa = false)"
        @switch-to-manual="openManualOrder(null)"
        @review="openManualOrder"
      />
    </Transition>
    <Transition name="fade">
      <FactoryAssignModal
        v-if="assignFactoryModal"
        :orderItem="selectedOrderItem"
        @onSave="fetchOnSave(true)"
        @close="factoryModalClose"
        @handleOrderUpdated="handleOrderUpdated"
      />
    </Transition>
    <Transition name="fade">
      <PriceBreakdownModal
        v-if="priceBreakdownModal"
        :order_item="{
          ...(selectedOrderDetails?.items?.[0]
            ? { ...currentRow, ...selectedOrderDetails.items[0] }
            : currentRow),
          factory_products:
            selectedOrderDetails?.items?.[0]?.factory_products ??
            (currentRow as any)?.factory_products ??
            [],
        }"
        :order="selectedOrderDetails"
        :appUrl="appURL"
        @close="handlePriceBreakDownModalCloseEvent"
        @openOrderPriceModal="openOrderPriceModal"
        @fetchOnSave="fetchOnSave"
        @passiveOrderDataRefreshed="handlePassiveOrderDataRefreshed"
        :key="`pbm-${selectedOrderDetails?.id ?? 'none'}-${selectedOrderDetails?.items?.[0]?.id ?? currentRow?.id ?? 'none'}`"
      />
    </Transition>
    <Transition name="fade">
      <AddInternalNoteModal
        v-if="internalNoteModal && internalNoteRow"
        :order_item_id="internalNoteRow.id"
        :internal_comment="
          internalNoteRow.internal_comment ? JSON.parse(internalNoteRow.internal_comment) : null
        "
        @on-close="handleInternalNoteClose"
        @on-cancel="
          () => {
            internalNoteModal = false;
            internalNoteRow = null;
          }
        "
      />
    </Transition>
  </div>
</template>
<script setup lang="ts">
import DashboardActions from '@/components/Dashboard/DashboardActions.vue';
import DashboardCards from '@/components/Dashboard/DashboardCards.vue';
import DashboardFilters from '@/components/Dashboard/DashboardFilters.vue';
import DashboardMoreMenu from '@/components/Dashboard/DashboardMoreMenu.vue';
import DashboardOrderCard from '@/components/Dashboard/DashboardOrderCard.vue';
import DashboardSearchBox from '@/components/Dashboard/DashboardSearchBox.vue';
import type { AskResult, SuggestOrder } from '@/composables/useSearchSuggest';
import { useRouter } from 'vue-router';
import DashboardTabs from '@/components/Dashboard/DashboardTabs.vue';
import AddInternalNoteModal from '@/components/Dashboard/Modals/AddInternalNoteModal.vue';
import AssignOrderAdminModal from '@/components/Dashboard/Modals/AssignOrderAdminModal.vue';
import CreateManualOrderModal from '@/components/Dashboard/Modals/CreateManualOrderModal.vue';
import CreateOrderButton from '@/components/Dashboard/CreateOrderButton.vue';
import CreateWithElsaDrawer from '@/components/Dashboard/CreateWithElsaDrawer.vue';
import { elsaOrderAllowed, type ElsaOrderPrefill } from '@/composables/useOrderAssistant';
import ExportPriceModal from '@/components/Dashboard/Modals/ExportPriceModal.vue';
import FactoryAssignModal from '@/components/Dashboard/Modals/FactoryAssignModal.vue';
import PriceBreakdownModal from '@/components/Dashboard/Modals/PriceBreakdownModal.vue';
import OrderDetail from '@/components/Dashboard/Order/OrderDetail.vue';
import OrderTabStrip from '@/components/Dashboard/Order/OrderTabStrip.vue';
import ColumnPicker from '@/components/Shared/ColumnPicker.vue';
import DataTable from '@/components/Shared/DataTable.vue';
import type { DashboardItem } from '@/models/DashboardItem';
import { latestShippingPromise } from '@/utils/latestShippingPromise';
import { can, isSuperOrOrderAdmin, CustimooOrderFlowStatuses, formatShortDate } from '@/composables';
import { useDashboard } from '@/composables/useDashboard';
import { useMainStore } from '@/store';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useTabletSplit } from '@/composables/useTabletSplit';
import { ORDER_ADMIN_ROLES } from '@/utils/orderAdminRoles';
const store = useMainStore();

// AB#21984 — a suggested order opens the way a link to it does (the team chat
// bell, an email link): the dashboard reads order_id / order_item_id from the
// address once, on load, so the address is loaded rather than pushed.
const router = useRouter();
// AB#21984 — "ask in plain words": ELSA's reading of a sentence is applied
// through the Filters panel (so every filter is visible there and removable,
// and one this person's panel does not offer is not applied) and, when it
// names one this dashboard has, the tab.
// Gated on the backend's own flag (features.ask_elsa), which the endpoint
// enforces too, so the option never shows for someone the server would refuse.
const canAskElsa = computed(
  () => (store.features as { ask_elsa?: boolean } | undefined)?.ask_elsa === true
);
const filtersPanel = ref<{
  applyQuickFilters: (o: Record<string, unknown>) => string[];
  clearFilters: () => void;
} | null>(null);
const applyAsked = (result: AskResult): void => {
  if (result.tab && dashboardTabs.value.some((t) => t.apiKey === result.tab)) {
    void onTabChange(result.tab);
  }
  filtersPanel.value?.applyQuickFilters(result.quick_filters);
};

const openSuggestedOrder = (order: SuggestOrder): void => {
  const query: Record<string, string> = {
    order_id: String(order.order_id),
    order_item_id: String(order.order_item_id),
  };
  if (order.order_no) query.order_no = String(order.order_no).split('-')[0];
  window.location.assign(router.resolve({ name: 'dashboard', query }).href);
};

/** AB#21824 — Create with ELSA: shown only when the backend flag says this
 *  viewer holds create-order-with-elsa (seeded for no role) on top of
 *  manual-create-order; otherwise the plain Create Order button. ELSA drafts;
 *  the manual form, opened prefilled, is where a person presses Create. */
const createWithElsa = ref(false);
const canCreateWithElsa = computed(() => elsaOrderAllowed(store.features));
const elsaPrefill = ref<ElsaOrderPrefill | null>(null);
const openManualOrder = (prefill: ElsaOrderPrefill | null) => {
  createWithElsa.value = false;
  elsaPrefill.value = prefill;
  createManualOrder.value = true;
};
const {
  appURL,
  dashboardTabs,
  permissionLoaded,
  isPending,
  error,
  isFetching,
  datatable,
  currentTab,
  createManualOrder,
  assignFactoryModal,
  selectedOrderDetails,
  isSidepaneLoading,
  priceBreakdownModal,
  selectedOrderItem,
  internalNoteModal,
  internalNoteRow,
  currentRow,
  current_filters,
  filteredOrderDetailTabs,
  tableData,
  totalItems,
  columns,
  currentOrderItem,
  currentOrderItemIndex,
  dashboardData,
  sidebarOpen,
  paneDefaultTab,
  isDisabled,
  handlePriceBreakDownModalCloseEvent,
  factoryModalClose,
  fetchRecords,
  onTabChange,
  fetchOnSave,
  getQuickFilterData,
  handleCellClick,
  handleOrderMoved,
  handleOrderUpdated,
  handleInternalNoteClose,
  openOrderPriceModal,
  onDetailsClose,
  detailsTabChange,
  handleRowSelected,
  openDashboardActions,
  showDashboardActions,
  dashboardActionsRef,
  handleCloseAction,
  openDetails,
  selectedRow,
  exportOrderPrices,
  getShippmentBookingCsv,
  // AB#21279
  orderTabs,
  openOrderItemIds,
  activateOrderTab,
  closeOrderTab,
  closeAllOrderTabs,
  undoLastReplace,
  lastReplacedTab,
  setActiveTabDirty,
  paneExpanded,
  togglePaneExpanded,
  // AB#21240
  columnOptions,
  hiddenColumns,
  setHiddenColumns,
  columnWidths,
  setColumnWidths,
  resetColumnWidths,
  canResetColumns,
  applySidebarPreference,
  pageSizeOptions,
  pendingAction,
  clearPendingAction,
} = useDashboard();

// AB#22137 — tablet (768-1024px): an open order takes the whole width; see
// useTabletSplit.
const { isTablet, tabletList, listGivesWay, onToggleExpand, showOrder, onEscape } =
  useTabletSplit({ paneExpanded, togglePaneExpanded });
watch(() => [orderTabs.value.activeKey, selectedRow.value], showOrder);
// Opening the order that is already the active tab changes neither key nor
// row, so the click itself brings the order back.
const onCellClick = (...args: Parameters<typeof handleCellClick>) => {
  showOrder();
  return handleCellClick(...args);
};

// AB#21297 — the ⋯ menu opens these; see the template note above them.
const assignAdminModal = ref<InstanceType<typeof AssignOrderAdminModal> | null>(null);
const exportModal = ref<InstanceType<typeof ExportPriceModal> | null>(null);

/**
 * AB#20223 follow-up (BLOCKER 1) — the Price Breakdown modal's passive, on-open background-refresh
 * notify. Safe to route through the normal `fetchOnSave` sync here: unlike `DashboardActions.onFetch`,
 * `useDashboard`'s `fetchOnSave` never emits/closes the context menu, and the modal's `:key` below is
 * now identity-based (order id + item id) rather than full-content, so an in-place ledger patch no
 * longer forces a remount that would discard an in-progress edit.
 */
const handlePassiveOrderDataRefreshed = () => {
  void fetchOnSave(true);
};

/**
 * AB#21279 — Alt+1…5 switch tabs, Esc leaves the expanded view. Alt rather
 * than Ctrl because Ctrl+digit is the browser's own tab switch, and a
 * shortcut that fights the browser is a shortcut nobody trusts.
 */
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && onEscape(openDetails.value)) return;
  if (event.key === 'Escape' && paneExpanded.value && !isTablet.value) {
    togglePaneExpanded(false);
    return;
  }
  if (!event.altKey || event.ctrlKey || event.metaKey) return;
  const n = Number(event.key);
  if (!Number.isInteger(n) || n < 1 || n > 5) return;
  const tab = orderTabs.value.tabs[n - 1];
  if (!tab) return;
  event.preventDefault();
  void activateOrderTab(tab.key);
};
window.addEventListener('keydown', onKeydown);
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

onMounted(async () => {
  // AB#21240 — the dashboard is the widest table in the product, so it starts
  // with the menu collapsed unless this user has said otherwise. Applied here
  // rather than in the store so every other page keeps today's default.
  applySidebarPreference();
  await store.getFactories();
  await store.getOrderAdmins(ORDER_ADMIN_ROLES);
  await store.getUnassignedOrders();
  await store.getCSManagers();
  await store.getSalesRepresentatives();
});
</script>
<style lang="scss">
/*
  AB#21534 — .dash-btn, .dash-toolbar and .dash-search now live in
  assets/main.scss so the Users page shares the toolbar vocabulary without
  depending on this component having been loaded first.
*/

/* AB#22137 — on a phone the open order is a full screen of its own. */
@media (max-width: 767.98px) {
  .dash-pane {
    height: 100dvh;
    overflow: hidden;
    border-left: 0;
  }
}

.otab-undo {
  @apply fixed bottom-6 right-6 z-[10040] flex items-center gap-3 rounded-[8px] bg-[#191919] text-white
         text-sm px-4 py-3 shadow-lg;
}
.otab-undo-btn {
  @apply text-[var(--accent-on-dark)] font-semibold bg-transparent border-0 cursor-pointer;
}
.otab-undo-btn:hover {
  @apply underline;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 10030;
  width: 100vw;
  background-color: #00000095;
  display: flex;
  justify-content: center;
  align-items: center;
}
.tooltip-container {
  .tooltip {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s ease;
    transform: translateX(-30px) translateY(-3px);
    border-right: 4px solid var(--accent);
    color: black;
    box-shadow: 0px 0px 10px #999999;
    padding: 5px 4px;
    white-space: nowrap;
    border-radius: 4px;
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: -7px;
      border-width: 5px;
      border-style: solid;
      transform: translateY(-50%);
      border-color: transparent white transparent transparent;
    }
  }
  &:hover {
    .tooltip {
      opacity: 1;
      transform: translateX(40px) translateY(-3px);
      z-index: 1;
    }
  }
}

/* Slide in/out from right */
.slide-in-enter-active,
.slide-in-leave-active {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
}
.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-in-enter-to,
.slide-in-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.custom-dqc-checkbox {
  appearance: none;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--ink-mute);
  border-radius: 0.25rem; /* slightly rounded square */
  background-color: var(--surface);
  cursor: pointer;
  position: relative;
  transition: all 0.15s ease;
}

/* Hover effect */
.custom-dqc-checkbox:hover {
  border-color: #22c55e; /* green-500 */
}

/* Checked state */
.custom-dqc-checkbox:checked {
  background-color: #22c55e; /* green-500 */
  border-color: #22c55e; /* green border */
}

/* Tick mark */
.custom-dqc-checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -55%);
  color: #fff;
  font-size: 0.7rem;
  font-weight: bold;
}

/* Disabled */
.custom-dqc-checkbox:disabled {
  cursor: not-allowed;
}
/* Optional: nice Tailwind-like shadow and rounding */
.absolute.z-50 {
  @apply bg-surface rounded-lg shadow-lg;
}
</style>
